import { openDB } from 'idb';
import { DATA_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;
    this.db = await openDB(`cw-store-${this.accountId}`, DATA_VERSION, {
      upgrade(db) {
        db.createObjectStore('cache-keys');
        db.createObjectStore('inbox', { keyPath: 'id' });
        db.createObjectStore('label', { keyPath: 'id' });
        db.createObjectStore('team', { keyPath: 'id' });
      },
    });

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  // A closed or broken connection keeps failing; the next initDb reopens it.
  reset() {
    this.db?.close();
    this.db = null;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    // One transaction, so a failed write cannot leave the store cleared. A put
    // that throws synchronously does not abort the transaction by itself.
    const tx = this.db.transaction(modelName, 'readwrite');
    const items = Array.isArray(data) ? data : [data];
    const requests = [tx.store.clear()];
    try {
      items.forEach(item => requests.push(tx.store.put(item)));
    } catch (error) {
      tx.abort();
      await Promise.allSettled([...requests, tx.done]);
      throw error;
    }
    await Promise.all([...requests, tx.done]);
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    await Promise.all(
      Object.keys(cacheKeys).map(modelName =>
        this.db.put('cache-keys', cacheKeys[modelName], modelName)
      )
    );
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
